import {
  Component,
  computed,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';
import {
  BadgeComponent,
  CardComponent,
  ToggleComponent,
} from '@group24ag/flowkit';
import { environment } from '@environment/environment';
import {
  EditableFeature,
  FeatureCenterService,
} from '@core/feature-preview/dialog/services/feature-center.service';
import { FeaturePreviewStatus } from '@core/feature-preview/types/feature-preview';

@Component({
  selector: 'app-feature-list-item',
  encapsulation: ViewEncapsulation.None,
  imports: [ToggleComponent, BadgeComponent, CardComponent],
  styles: [
    `
      app-feature-list-item .not-selectable .flkCard__container {
        cursor: not-allowed;
        opacity: 60%;
      }

      app-feature-list-item
        .feature-card:not(.not-selectable)
        .flkCard__container {
        cursor: pointer;
      }

      app-feature-list-item
        .feature-card:not(.not-selectable)
        .flkCard__container:hover {
        border-color: var(--color-greenish-300);
        box-shadow: 0 0 4px 2px var(--color-greenish-500);
      }

      app-feature-list-item
        .feature-card:not(.not-selectable)
        .flkCard__container:is(.dark *):hover {
        border-color: var(--color-greenish-700);
        box-shadow: 0 0 4px 2px var(--color-greenish-500);
      }
    `,
  ],
  template: `
    <flk-card
      class="feature-card h-full cursor-pointer"
      [class.not-selectable]="unavailable() || live()"
      (click)="toggleEnabled()">
      <div class="flex w-full items-start justify-between gap-x-1">
        <div class="flex items-center gap-x-2">
          <h3 class="text--contrast-highest text-lg font-bold">
            {{ featurePreview().name }}
          </h3>

          @if (unavailable()) {
            <span class="text-xs italic"> (Unavailable) </span>
          } @else if (live()) {
            <span class="text-xs italic"> (Live) </span>
          }
        </div>

        <flk-toggle
          class="mt-1"
          [disabled]="unavailable()"
          [checked]="enabled()"
          (toggleChange)="toggleEnabled()"
          (click)="$event.stopPropagation()" />
      </div>

      <p class="text--contrast-low pt-2 text-xs">
        {{ featurePreview().description }}
      </p>

      @let tags = featurePreview().tags;
      @let envs = featurePreview().environments;
      @if (tags?.length || (unavailable() && envs?.length)) {
        <div class="flex flex-wrap gap-x-1.5 gap-y-0.5 pt-5">
          @for (tag of tags; track $index) {
            <flk-badge
              class="h-7"
              variant="info"
              [label]="tag.toUpperCase()"
              [hideRemove]="true" />
          }

          @if (unavailable()) {
            @for (tag of envs; track $index) {
              <flk-badge
                class="h-7"
                variant="accent"
                [label]="tag.toUpperCase()"
                [hideRemove]="true" />
            }
          }
        </div>
      }
    </flk-card>
  `,
})
export class FeatureListItemComponent {
  /** INJECTIONS **/
  /****/
  private readonly centerService = inject(FeatureCenterService);

  /** INPUTS **/
  /****/
  readonly feature = input.required<EditableFeature>();

  /** PROPERTIES **/
  /****/
  protected readonly featurePreview = computed(() => this.feature().feature);

  /****/
  protected readonly enabled = computed(() => this.feature().enabled);

  /** Set to true if the feature is disabled for the current environment; Overrules isEnabled */
  protected readonly unavailable = computed(() => {
    const environments = this.featurePreview().environments || [];
    return !!environments.length && !environments.includes(environment.name);
  });

  /****/
  protected readonly live = computed(
    () => this.featurePreview().status === FeaturePreviewStatus.LIVE
  );

  protected toggleEnabled() {
    if (this.unavailable() || this.live()) return;
    this.centerService.toggleFeature(this.featurePreview().key);
  }
}
